/* eslint-disable @typescript-eslint/ban-types */
import store from 'store';
import * as actions from './actions';
import { environment } from '../../../environments/environment';
import { AppState, AppStateSettings } from './AppState';
import { DeveloperHardwareConfig } from './DeveloperHardwareConfig';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { ProjectDto } from '@api/projects/interfaces';
import { IParticipant } from '@interfaces/interfaces';

function RETRIEVE<T>(base: string, storedSettings: T): T {
	const settings = {} as T;
	Object.keys(storedSettings).forEach(key => {
		const item = store.get(`${base}.${key}`);
		settings[key] = typeof item !== 'undefined' ? item : storedSettings[key];
	});
	return settings;
}

function STORE<T>(base: string, state: T): T {
	const keys = Object.keys(state);
	keys.forEach((key) => {
		store.set(`${base}.${key}`, state[key]);
	});
	return state;
}

const STORED_SETTINGS = (storedSettings: object) => {
	return RETRIEVE('app.settings', storedSettings);
};

const STORED_SETTINGS_DEVELOP = (storedDevSettings: DeveloperHardwareConfig): DeveloperHardwareConfig => {
	if (environment.production && !environment.demo) {
		return {} as DeveloperHardwareConfig;
	}
	return RETRIEVE('app.devsettings', storedDevSettings);
};

const STORED_SETTINGS_APP_STATE = (storedAppStateSettings: AppStateSettings): AppStateSettings => {
	return RETRIEVE('app.appstatesettings', storedAppStateSettings);
};

export function storeMetaReducer<T>(base: string, reducer: ActionReducer<T>): ActionReducer<T> {
	return (state: T, action: Action): T => {
		const newState = reducer(state, action);
		return STORE(base, newState);
	};
}

export const initialState: object = {
	// default settings, if not exist in localStorage
	...STORED_SETTINGS({
		authProvider: 'jwt', // firebase, jwt
		locale: 'de-DE',
		isSidebarOpen: false,
		isDevSidebarOpen: false,
		isSupportChatOpen: false,
		isMobileView: false,
		isMobileMenuOpen: false,
		isMenuCollapsed: false,
		menuLayoutType: 'left', // left, top, nomenu
		menuColor: 'white', // white, dark, gray
		theme: 'default', // default, dark
		authPagesColor: 'white', // white, gray, image
		primaryColor: '#4b7cf3',
		topBarHeight: 64,
		leftMenuWidth: 256,
		leftMenuWidthCollapsed: 80,
		isMenuUnfixed: false,
		isMenuShadow: false,
		isGrayTopbar: false,
		isContentMaxWidth: false,
		isAppMaxWidth: false,
		isGrayBackground: false,
		isCardShadow: true,
		isSquaredBorders: false,
		isBorderless: false,
		is_privacy_protected: false,
	}),
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface DeveloperSettings extends DeveloperHardwareConfig {}
export const initialDevState: DeveloperSettings = {
	...STORED_SETTINGS_DEVELOP({
		overrideHardwareStatus: false,
		emulateTouchScreen: false,
		emulateButtonDevice: false,
		emulateTappingPen: false,
		emulateExternalAudiometry: false,
		emulateSoundDevice: false,
		emulateAudiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB: false,
		emulateAudiometry_bd_DT_770_PRO_32__Sb_X_G1: false,
		emulateExternalBicycleErgometer: false,
		emulateTestHardware: false,
	}),
};

export const initialAppState: AppStateSettings =
	STORED_SETTINGS_APP_STATE({
		activeParticipantID: null,
		activeProjectID: null,
	});

export function reducer(state = initialState, action: actions.Actions): object {
	switch (action.type) {
		case actions.SET_STATE: {
			const keys = Object.keys(action.payload);
			keys.forEach((key) => {
				store.set(`app.settings.${key}`, action.payload[key]);
			});
			return { ...state, ...action.payload };
		}
		default:
			return state;
	}
}

export function devReducer(state = initialDevState, action: actions.Actions): DeveloperSettings {
	switch (action.type) {
		case actions.SET_DEV_STATE: {
			if (environment.production && !environment.demo)
				return state;

			const keys = Object.keys(action.payload);
			keys.forEach((key) => {
				store.set(`app.devsettings.${key}`, action.payload[key]);
			});
			return { ...state, ...action.payload };
		}
		default:
			return state;
	}
}

export const appStateSettingsReducer = storeMetaReducer('app.appstatesettings', createReducer(
	initialAppState,
	on(actions.appStateSetAction, (state, newState) => ({...newState})),
	on(actions.appStateChangeAction, (state, newState) => ({...state, ...newState})),
	on(actions.activeProjectChange, (state, { activeProjectID }) => ({...state, activeProjectID})),
	on(actions.activeParticipantChange, (state, { activeParticipantID }) => ({...state, activeParticipantID})),
));

export const appStateReducer = createReducer(
	{} as AppState,
	on(actions.activeProjectChanged, (state, { project }) => ({...state, activeProject: project })),
	on(actions.activeParticipantChanged, (state, { participant }) => ({...state, activeParticipant: participant })),
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export const getSettings = (state: any): any => state;
export const getDevSettings = (state: DeveloperSettings): DeveloperSettings => state;
export const getAppStateSettings = (state: AppStateSettings): AppStateSettings => state;
export const getActiveProjectID = (state: AppStateSettings): string => state.activeProjectID;

export const getActiveProject = (state: AppState): ProjectDto => state.activeProject;
export const getActiveParticipant = (state: AppState): IParticipant => state.activeParticipant;
